import React from 'react';
import { Check, Folder, Tag, UserX, X } from 'lucide-react';
import { MobileFullScreenModal, ModalDoneFooter } from '../../ui/MobileFullScreenModal';
import { ProjectFilter, UserFilter } from '../types/dashboardTypes';
import { FILTER_CONSTANTS } from '../constants/filterConstants';

interface MobileDashboardFilterDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  userFilters: UserFilter[];
  projectFilters: ProjectFilter[];
  toggleUserFilter: (userId: string, isExclusive?: boolean) => void;
  toggleProjectFilter: (projectId: string, isExclusive?: boolean) => void;
  availableTags?: string[];
  selectedTags?: string[];
  onTagsChange?: (tags: string[]) => void;
}

/**
 * Mobile filter dropdown for the dashboard
 * Displays users, projects, and tags in a full-screen modal
 */
export function MobileDashboardFilterDropdown({
  isOpen,
  onClose,
  userFilters,
  projectFilters,
  toggleUserFilter,
  toggleProjectFilter,
  availableTags = [],
  selectedTags = [],
  onTagsChange = () => {},
}: MobileDashboardFilterDropdownProps) {
  // Check if shared-unassigned is active from the userFilters
  const isSharedUnassignedActive = userFilters.some(
    filter => filter.id === FILTER_CONSTANTS.SHARED_UNASSIGNED && filter.active
  );
  
  if (!isOpen) return null;

  return (
    <MobileFullScreenModal
      isOpen={isOpen}
      onClose={onClose}
      title="Filters"
      footerContent={<ModalDoneFooter onClose={onClose} />}
    >
      {/* Users section - moved to the top */}
      {userFilters.length > 0 && (
        <div>
          <div className="px-4 py-3 bg-gray-50 flex justify-between items-center">
            <h3 className="font-medium text-gray-700">People</h3>
            {userFilters.some(f => f.active) && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  userFilters.forEach(filter => {
                    if (filter.active) toggleUserFilter(filter.id, true);
                  });
                }}
                className="text-blue-400 hover:text-blue-600 transition-colors"
                aria-label="Clear user selection"
              >
                <X size={16} />
              </button>
            )}
          </div>
          {userFilters.map(filter => (
            <div
              key={filter.id}
              className="w-full flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-[var(--content-bg-color)] cursor-pointer"
              onClick={() => toggleUserFilter(filter.id, true)}
            >
              <div className="flex-1 flex items-center gap-3">
                {filter.avatar_url ? (
                  <img
                    src={filter.avatar_url}
                    alt={filter.name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium">
                    {filter.name?.charAt(0) || '?'}
                  </div>
                )}
                <span className="font-medium text-base">{filter.name}</span>
              </div>
              <div
                className={`w-5 h-5 flex items-center justify-center rounded-full border ${
                  filter.active ? 'bg-green-500 border-green-500' : 'border-gray-300'
                }`}
              >
                {filter.active && <Check size={14} className="text-white" />}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Special filters section - Shared & Unassigned */}
      <div className="border-t border-gray-100">
        <div className="px-4 py-3 bg-gray-50 flex justify-between items-center">
          <h3 className="font-medium text-gray-700">Other</h3>
          {isSharedUnassignedActive && (
            <button
              onClick={e => {
                e.stopPropagation();
                toggleUserFilter(FILTER_CONSTANTS.SHARED_UNASSIGNED, true);
              }}
              className="text-blue-400 hover:text-blue-600 transition-colors"
              aria-label="Clear shared-unassigned selection"
            >
              <X size={16} />
            </button>
          )}
        </div>
        <div
          className="w-full flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-[var(--content-bg-color)] cursor-pointer"
          onClick={() => toggleUserFilter(FILTER_CONSTANTS.SHARED_UNASSIGNED, true)}
        >
          <div className="flex-1 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium">
              <UserX size={16} className="text-blue-500" />
            </div>
            <span className="font-medium text-base">Shared & unassigned</span>
          </div>
          <div
            className={`w-5 h-5 flex items-center justify-center rounded-full border ${
              isSharedUnassignedActive ? 'bg-green-500 border-green-500' : 'border-gray-300'
            }`}
          >
            {isSharedUnassignedActive && <Check size={14} className="text-white" />}
          </div>
        </div>
      </div>

      {/* Projects section */}
      {projectFilters.length > 0 && (
        <div className="border-t border-gray-100">
          <div className="px-4 py-3 bg-gray-50 flex justify-between items-center">
            <h3 className="font-medium text-gray-700">Projects</h3>
            {projectFilters.some(f => f.active) && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  projectFilters.forEach(filter => {
                    if (filter.active) toggleProjectFilter(filter.id, true);
                  });
                }}
                className="text-blue-400 hover:text-blue-600 transition-colors"
                aria-label="Clear project selection"
              >
                <X size={16} />
              </button>
            )}
          </div>
          {projectFilters.map(filter => (
            <div
              key={filter.id}
              className="w-full flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-[var(--content-bg-color)] cursor-pointer"
              onClick={() => toggleProjectFilter(filter.id, true)}
            >
              <div className="flex-1 truncate pr-6 flex items-center gap-2">
                <Folder size={16} className="text-blue-500 flex-shrink-0" />
                <span className="text-base">{filter.name}</span>
              </div>
              <div
                className={`w-5 h-5 flex items-center justify-center rounded-full border ${
                  filter.active ? 'bg-green-500 border-green-500' : 'border-gray-300'
                }`}
              >
                {filter.active && <Check size={14} className="text-white" />}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tags section */}
      {availableTags.length > 0 && (
        <div className="border-t border-gray-100">
          <div className="px-4 py-3 bg-gray-50 flex justify-between items-center">
            <h3 className="font-medium text-gray-700">Tags</h3>
            {selectedTags.length > 0 && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  onTagsChange([]);
                }}
                className="text-blue-400 hover:text-blue-600 transition-colors"
                aria-label="Clear tag selection"
              >
                <X size={16} />
              </button>
            )}
          </div>
          {availableTags.map(tag => (
            <div
              key={tag}
              className="w-full flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-[var(--content-bg-color)] cursor-pointer"
              onClick={() => {
                if (selectedTags.includes(tag)) {
                  onTagsChange(selectedTags.filter(t => t !== tag));
                } else {
                  onTagsChange([...selectedTags, tag]);
                }
              }}
            >
              <div className="flex-1 truncate pr-6 flex items-center gap-2">
                <Tag size={16} className="text-blue-500 flex-shrink-0" />
                <span className="text-base">{tag}</span>
              </div>
              <div
                className={`w-5 h-5 flex items-center justify-center rounded-full border ${
                  selectedTags.includes(tag) ? 'bg-green-500 border-green-500' : 'border-gray-300'
                }`}
              >
                {selectedTags.includes(tag) && <Check size={14} className="text-white" />}
              </div>
            </div>
          ))}
        </div>
      )}
    </MobileFullScreenModal>
  );
}

export default MobileDashboardFilterDropdown;
